import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import {
  Grid,
  IconGraphy,
  Button,
  Dropdown,
  Tab,
  Typography,
  Badge,
  Table,
  Loading,
  Tooltip,
} from '@leapeasy/ui-kit';
import { InvitationConfirmDialog } from 'components/Email/InvitationConfirmDialog';
import styled from 'styled-components';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteEmailAction, getEmailAction } from 'store/actions/emailActions';
import { getUserBuildingAction } from 'store/actions/userActions';
import { successNotif, failedNotif } from 'store/actions/notifActions';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { openDetails } from 'store/actions/uiActions';
import { inviteUserAction } from 'store/actions/inviteActions';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { getEmail } from 'store/sagas/APIs/email';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildMailboxListFilterChips,
  parseMailboxListFilterChipId,
} from 'utils/listTableActiveFilterChips';
import { GlobalUXState } from 'components/GlobalUXState';
import { useDemoMask } from 'hooks/useDemoMask';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import { buildMaskedInvitationEmailRow } from 'utils/demoMask';
import { readStoredUser } from 'utils/authSession';
import {
  INVITATION_MAILS_TABLE_DEFAULT_VISIBLE_KEYS,
  INVITATION_MAILS_TABLE_STORAGE_KEY,
  canResendInvitation,
} from './invitationMailsTableConfig';

const LEAP_PURPLE = '#702572';

const dateColumnOptions = (overrides = {}) => ({
  width: 104,
  minWidth: 104,
  maxWidth: 104,
  shrink: false,
  ...overrides,
});

const parseEmailFilters = (searchParams, _paramKey, fallback) => ({
  selectedFromEmail: searchParams.get('fromEmail')
    ? searchParams.get('fromEmail').split(',').filter(Boolean)
    : fallback.selectedFromEmail,
  selectedToEmail: searchParams.get('toEmail')
    ? searchParams.get('toEmail').split(',').filter(Boolean)
    : fallback.selectedToEmail,
  selectedProperty: searchParams.get('property')
    ? searchParams.get('property').split(',').filter(Boolean)
    : fallback.selectedProperty,
});

const serializeEmailFilters = (filters) => ({
  fromEmail:
    Array.isArray(filters.selectedFromEmail) && filters.selectedFromEmail.length > 0
      ? filters.selectedFromEmail.join(',')
      : '',
  toEmail:
    Array.isArray(filters.selectedToEmail) && filters.selectedToEmail.length > 0
      ? filters.selectedToEmail.join(',')
      : '',
  property:
    Array.isArray(filters.selectedProperty) && filters.selectedProperty.length > 0
      ? filters.selectedProperty.join(',')
      : '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const EMPTY_EMAIL_LIST_FILTER = {
  options: {},
  searchText: '',
  searchPlaceholder: 'Search Invitation..',
};

const filterInvitationsByMailbox = (records, page, userEmail) => {
  if (!Array.isArray(records)) return [];
  if (page === 'all') return records;
  if (page === 'sent') {
    return records.filter((e) => (e.requestor ? e.requestor.username : '') === userEmail);
  }
  if (page === 'inbox') {
    return records.filter((e) => e.requestor_email === userEmail);
  }
  return records;
};

const mapInvitationToTableRow = (record) => [
  record.request_type,
  record.accepter_email,
  record.requestor_email,
  record.requestor ? record.requestor.username : '',
  record.property.length,
  new Date(record.updatedAt).toISOString().slice(0, 10),
  record.request_status,
  record.accepter_role,
  record.requestor_role,
  {
    id: record.id,
    request_status: record.request_status,
  },
  record.id,
];

const TABS = [
  {
    title: 'All Invites',
    icon: 'Notifications.Email',
  },
  {
    title: 'Inbox',
    icon: 'Notifications.Inbox',
  },
  {
    title: 'Sent',
    icon: 'EditorLayout.Send',
  },
];
const listEmailType = [
  {
    text: 'All',
    value: '',
  },
  {
    text: 'Invite',
    value: 'invite',
  },
  {
    text: 'Reinvite',
    value: 'reinvite',
  },
];

export const Email = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDemo } = useDemoMask();
  const [tableData, setTableData] = useState([]);
  const [selectedForResend, setSelectedForResend] = useState(null);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [awaitingDelete, setAwaitingDelete] = useState(false);
  const [awaitingResend, setAwaitingResend] = useState(false);
  const actionLoading = useSelector((state) => state.getIn(['auth', 'loading']));
  const [currentPage, setCurrentPage] = useState('all');

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Invitation..',
    },
    defaultSortOptions: { updatedAt: -1 },
    extraFiltersParam: 'filters',
    defaultExtraFilters: {
      selectedFromEmail: [],
      selectedToEmail: [],
      selectedProperty: [],
    },
    parseExtraFilters: parseEmailFilters,
    serializeExtraFilters: serializeEmailFilters,
  });

  const [listFromEmail, setListFromEmail] = useState([]);
  const [listToEmail, setListToEmail] = useState([]);
  const [listProperty, setListProperty] = useState([]);

  // selected values
  const [selectedEmailType, setSelectedEmailType] = useState([]);
  const [selectedFromEmail, setSelectedFromEmail] = useState([]);
  const [selectedToEmail, setSelectedToEmail] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState([]);
  const [selectedFromEmailDraft, setSelectedFromEmailDraft] = useState([]);
  const [selectedToEmailDraft, setSelectedToEmailDraft] = useState([]);
  const [selectedPropertyDraft, setSelectedPropertyDraft] = useState([]);

  const [filteredEmail, setFilteredEmail] = useState([]);

  const [selectedTab, setSelectedTab] = useState(0);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const emailTableRef = useRef(null);
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedFromEmail = Array.isArray(extraFilters.selectedFromEmail)
      ? extraFilters.selectedFromEmail
      : [];
    const nextSelectedToEmail = Array.isArray(extraFilters.selectedToEmail)
      ? extraFilters.selectedToEmail
      : [];
    const nextSelectedProperty = Array.isArray(extraFilters.selectedProperty)
      ? extraFilters.selectedProperty
      : [];
    setSelectedFromEmail((prev) =>
      areArraysEqual(prev, nextSelectedFromEmail) ? prev : nextSelectedFromEmail,
    );
    setSelectedToEmail((prev) =>
      areArraysEqual(prev, nextSelectedToEmail) ? prev : nextSelectedToEmail,
    );
    setSelectedProperty((prev) =>
      areArraysEqual(prev, nextSelectedProperty) ? prev : nextSelectedProperty,
    );
    setSelectedFromEmailDraft((prev) =>
      areArraysEqual(prev, nextSelectedFromEmail) ? prev : nextSelectedFromEmail,
    );
    setSelectedToEmailDraft((prev) =>
      areArraysEqual(prev, nextSelectedToEmail) ? prev : nextSelectedToEmail,
    );
    setSelectedPropertyDraft((prev) =>
      areArraysEqual(prev, nextSelectedProperty) ? prev : nextSelectedProperty,
    );
  }, [extraFilters]);

  const emails = useSelector((state) => state.getIn(['email', 'data']));
  const emailsJSON = emails && emails.toJS();
  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(emailsJSON?.total) || 0,
    isLoading: Boolean(emailsJSON?.isLoading),
  });

  const currentUser = readStoredUser();
  const userEmail = currentUser?.email;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getEmail({
          filter: EMPTY_EMAIL_LIST_FILTER,
          offset: 0,
          limit: 1,
          sortOptions,
          selectedTab: 0,
          selectedFromEmail: [],
          selectedToEmail: [],
          selectedProperty: [],
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore prefetch failures
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(emailsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getEmail({
            filter,
            offset,
            limit,
            sortOptions,
            selectedTab,
            selectedFromEmail,
            selectedToEmail,
            selectedProperty,
          }),
      });
      const scoped = filterInvitationsByMailbox(rows, currentPage, userEmail);
      return scoped.map(mapInvitationToTableRow);
    } catch (error) {
      throw new Error('Unable to export filtered emails right now.');
    }
  }, [
    emailsJSON?.total,
    filter,
    sortOptions,
    selectedTab,
    selectedFromEmail,
    selectedToEmail,
    selectedProperty,
    currentPage,
    userEmail,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords = Number(fullExportDatasetTotal) || Number(emailsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getEmail({
            filter: EMPTY_EMAIL_LIST_FILTER,
            offset,
            limit,
            sortOptions,
            selectedTab: 0,
            selectedFromEmail: [],
            selectedToEmail: [],
            selectedProperty: [],
          }),
      });
      return filterInvitationsByMailbox(rows, 'all', userEmail).map(mapInvitationToTableRow);
    } catch (error) {
      throw new Error('Unable to export all emails right now.');
    }
  }, [fullExportDatasetTotal, emailsJSON?.total, sortOptions, userEmail]);

  const handleDispatch = useCallback(() => {
    dispatch(
      getEmailAction({
        filter,
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions: sortOptions,
        selectedTab,
        selectedFromEmail,
        selectedToEmail,
        selectedProperty,
      }),
    );
  }, [
    dispatch,
    filter,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
    selectedTab,
    selectedFromEmail,
    selectedToEmail,
    selectedProperty,
  ]);

  useEffect(() => {
    userEmail && handleDispatch();
  }, [userEmail, handleDispatch]);

  const { hasSuccess: hasActionSuccess, hasError: hasActionError } = useNotificationMessages();

  useEffect(() => {
    if (!awaitingDelete || !hasActionSuccess) return;
    setSelectedForDelete(null);
    setAwaitingDelete(false);
  }, [awaitingDelete, hasActionSuccess]);

  useEffect(() => {
    if (!awaitingResend || !hasActionSuccess) return;
    setSelectedForResend(null);
    setAwaitingResend(false);
  }, [awaitingResend, hasActionSuccess]);

  useEffect(() => {
    if (hasActionError) {
      setAwaitingDelete(false);
      setAwaitingResend(false);
    }
  }, [hasActionError]);

  useEffect(() => {
    if (emails && !emailsJSON?.isLoading) {
      const emailData = emailsJSON || {};
      const fromEmails = Array.isArray(emailData.fromEmails) ? emailData.fromEmails : [];
      const toEmails = Array.isArray(emailData.toEmails) ? emailData.toEmails : [];
      const properties = Array.isArray(emailData.properties) ? emailData.properties : [];
      const rows = Array.isArray(emailData.data) ? emailData.data : [];
      setListFromEmail(
        fromEmails.map((item) => ({ text: item.accepter_email, value: item.accepter_email })),
      );
      setListToEmail(
        toEmails.map((item) => ({ text: item.requestor_email, value: item.requestor_email })),
      );
      setListProperty(
        properties.map((item) => ({ text: item.unique_property, value: item.unique_property })),
      );

      if (currentPage === 'all') {
        setFilteredEmail(rows);
      } else if (currentPage === 'sent') {
        setFilteredEmail(rows.filter((item) => item[5] === userEmail));
      } else if (currentPage === 'inbox') {
        setFilteredEmail(rows.filter((item) => item[3] === userEmail));
      }
    }
  }, [emails, currentPage, userEmail]);

  useEffect(() => {
    if (emails) {
      const emailData = [];
      filteredEmail.forEach((email, value) => {
        emailData.push(buildMaskedInvitationEmailRow(email, isDemo, value));
      });
      setTableData([...emailData]);
    }
  }, [filteredEmail, isDemo]);

  const goDetailPage = useCallback(
    (emailId) => {
      const selectedBuilding = emails?.toJS?.()?.data?.find((email) => email.id === emailId);
      if (!selectedBuilding?.id) return;
      dispatch(getUserBuildingAction({ id: selectedBuilding.property }));
      dispatch(
        openDetails({
          type: 'email',
          data: selectedBuilding,
        }),
      );
      navigate(`/invitations/${selectedBuilding.id}/detail`);
    },
    [dispatch, emails, navigate],
  );

  const renderTextCell = useCallback(
    (value) => (
      <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
        {value}
      </CustomTypography>
    ),
    [],
  );

  const columns = useMemo(
    () => [
      {
        name: 'Type',
        key: 'type',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 100, maxWidth: 120 }),
          sort: true,
          customBodyRenderer: (value) => {
            const isInvite = value === 'invite';
            return (
              <Badge
                background="#F3F1F4"
                color={isInvite ? 'purple' : 'sunglow'}
                label={isInvite ? 'Invite' : 'Re-invite'}
                rounded
                textSize="medium"
              />
            );
          },
        },
      },
      {
        name: 'From Email',
        key: 'accepter_email',
        options: {
          minWidth: 160,
          flexGrow: 1,
          ellipsis: false,
          sort: true,
          customBodyRenderer: renderTextCell,
        },
      },
      {
        name: 'To Email',
        key: 'requestor_email',
        options: {
          ...fitContentTextColumnOptions({ minWidth: 160, maxWidth: 300, flexGrow: 0 }),
          sort: true,
          customBodyRenderer: renderTextCell,
        },
      },
      {
        name: 'Name',
        key: 'name',
        options: {
          ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 260, flexGrow: 0 }),
          sort: true,
          customBodyRenderer: renderTextCell,
        },
      },
      {
        name: 'Property Count',
        key: 'n_building',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 110, align: 'center' }),
          sort: true,
          customBodyRenderer: renderTextCell,
        },
      },
      {
        name: 'Updated date',
        key: 'updatedAt',
        options: {
          ...dateColumnOptions(),
          sort: true,
          customBodyRenderer: renderTextCell,
        },
      },
      {
        name: 'Status',
        key: 'status',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 100, maxWidth: 140 }),
          sort: true,
          customBodyRenderer: (value) => (
            <Badge
              background="rgba(243, 241, 244, 1)"
              color="tomato"
              label={value}
              rounded
              textSize="medium"
            />
          ),
        },
      },
      {
        name: 'To Role',
        key: 'accepter_role',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 140 }),
          sort: true,
          customBodyRenderer: (value) => (
            <Badge
              background="rgba(243, 241, 244, 1)"
              color="tomato"
              label={value}
              rounded
              textSize="medium"
            />
          ),
        },
      },
      {
        name: 'From Role',
        key: 'requestor_role',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 140 }),
          sort: true,
          customBodyRenderer: (value) => (
            <Badge
              background="rgba(243, 241, 244, 1)"
              color="tomato"
              label={value}
              rounded
              textSize="medium"
            />
          ),
        },
      },
      {
        name: 'Resend Invite',
        key: 'resend_invite',
        options: {
          width: 100,
          minWidth: 100,
          maxWidth: 100,
          shrink: false,
          sort: false,
          align: 'center',
          excludeFromExport: true,
          customBodyRenderer: (value) => (
            <ResendInviteCell>
              <Tooltip title="Resend invite" placement="top" showCarot>
                <span>
                  <Button
                    variant="secondary"
                    iconSuffix="EditorLayout.Send"
                    disabled={!canResendInvitation(value?.request_status)}
                    onClick={() => setSelectedForResend(value.id)}
                  >
                    Resend
                  </Button>
                </span>
              </Tooltip>
            </ResendInviteCell>
          ),
        },
      },
      {
        name: 'Actions',
        key: 'actions',
        options: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          shrink: false,
          sort: false,
          align: 'center',
          excludeFromExport: true,
          stickyRight: true,
          customBodyRenderer: (emailId) => (
            <ActionsCell onClick={(e) => e.stopPropagation()}>
              <Tooltip title="View invitation details" placement="top" showCarot>
                <ActionIconButton
                  type="button"
                  onClick={() => goDetailPage(emailId)}
                  aria-label="View invitation details"
                >
                  <IconGraphy icon="FileFolder.StickyNote2" style={{ color: LEAP_PURPLE }} />
                </ActionIconButton>
              </Tooltip>
              <Tooltip title="Delete invitation" placement="top" showCarot>
                <ActionIconButton
                  type="button"
                  onClick={() => setSelectedForDelete(emailId)}
                  aria-label="Delete invitation"
                >
                  <IconGraphy icon="General.Delete" style={{ color: LEAP_PURPLE }} />
                </ActionIconButton>
              </Tooltip>
            </ActionsCell>
          ),
        },
      },
    ],
    [goDetailPage, renderTextCell],
  );

  const buildListQuery = useCallback(
    () => ({
      filter,
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions,
      selectedTab,
      selectedFromEmail,
      selectedToEmail,
      selectedProperty,
    }),
    [
      filter,
      pagination.pageNumber,
      pagination.rowsPerPage,
      sortOptions,
      selectedTab,
      selectedFromEmail,
      selectedToEmail,
      selectedProperty,
    ],
  );

  const handleResend = () => {
    if (!selectedForResend) return;
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingResend(true);
    dispatch(
      inviteUserAction({
        emailSubject: "You've been Invited to the Leap Portal",
        type: 'resend-email',
        id: selectedForResend,
      }),
    );
  };

  const handleDelete = () => {
    if (!selectedForDelete) return;
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingDelete(true);
    dispatch(
      deleteEmailAction({
        id: selectedForDelete,
        listQuery: buildListQuery(),
      }),
    );
  };

  const resetFunc = useCallback(() => {
    setSelectedEmailType([]);
    setSelectedFromEmail([]);
    setSelectedToEmail([]);
    setSelectedProperty([]);
    setSelectedFromEmailDraft([]);
    setSelectedToEmailDraft([]);
    setSelectedPropertyDraft([]);
    setExtraFilters({
      selectedFromEmail: [],
      selectedToEmail: [],
      selectedProperty: [],
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const applyFunc = useCallback(() => {
    setSelectedFromEmail(selectedFromEmailDraft);
    setSelectedToEmail(selectedToEmailDraft);
    setSelectedProperty(selectedPropertyDraft);
    setExtraFilters({
      selectedFromEmail: selectedFromEmailDraft,
      selectedToEmail: selectedToEmailDraft,
      selectedProperty: selectedPropertyDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedFromEmailDraft,
    selectedToEmailDraft,
    selectedPropertyDraft,
    setExtraFilters,
    setPagination,
  ]);

  const listFilterChips = useMemo(
    () =>
      buildMailboxListFilterChips({
        selectedFromEmail,
        selectedToEmail,
        selectedProperty,
      }),
    [selectedFromEmail, selectedToEmail, selectedProperty],
  );

  const handleRemoveMailboxListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseMailboxListFilterChipId(chipId);
      if (!parsed) return;
      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      if (parsed.type === 'fromEmail') {
        const next = selectedFromEmail.filter((s) => s !== parsed.value);
        setSelectedFromEmail(next);
        setSelectedFromEmailDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedFromEmail: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'toEmail') {
        const next = selectedToEmail.filter((s) => s !== parsed.value);
        setSelectedToEmail(next);
        setSelectedToEmailDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedToEmail: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'property') {
        const next = selectedProperty.filter((s) => s !== parsed.value);
        setSelectedProperty(next);
        setSelectedPropertyDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedProperty: next }));
        bumpPage();
      }
    },
    [selectedFromEmail, selectedToEmail, selectedProperty, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveMailboxListFilterChip,
    resetFunc,
  );

  return (
    <DashboardLayoutContainer>
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" height="32px">
          <Box display="flex" alignItems="center" gap="8px">
            <InvitationConfirmDialog
              type="resend"
              open={Boolean(selectedForResend)}
              onClose={() => {
                if (awaitingResend) return;
                setSelectedForResend(null);
              }}
              onConfirm={handleResend}
              isSubmitting={actionLoading && awaitingResend}
            />
            <InvitationConfirmDialog
              type="delete"
              open={Boolean(selectedForDelete)}
              onClose={() => {
                if (awaitingDelete) return;
                setSelectedForDelete(null);
              }}
              onConfirm={handleDelete}
              isSubmitting={actionLoading && awaitingDelete}
            />
          </Box>
          {/* )} */}
        </Box>
        <Grid>
          <Box display="flex" width="100%">
            <Box display="flex">
              {TABS.map((tab, index) => (
                <Tab
                  title={tab.title}
                  icon={tab.icon}
                  isSelected={selectedTab === index}
                  onClick={() => setSelectedTab(index)}
                />
              ))}
            </Box>
          </Box>
        </Grid>
        <Box marginTop="20px">
          {emails ? (
            <React.Fragment>
              <CustomBox>
                {emailsJSON.isLoading && (
                  <LoadingEffect>
                    <Loading size={'large'} />
                  </LoadingEffect>
                )}
                <Table
                  showEmptyState={!emailsJSON.isLoading}
                  emptyContent={
                    <GlobalUXState
                      variant={filter?.searchText ? 'noResults' : 'noData'}
                      compact
                      description={
                        filter?.searchText
                          ? 'No emails match your search or filters.'
                          : 'No emails available yet.'
                      }
                    />
                  }
                  ref={emailTableRef}
                  onExportButtonClick={() => setExportModalOpen(true)}
                  onRequestExportData={handleRequestExportData}
                  onRequestFullExportData={handleRequestFullExportData}
                  data={tableData}
                  filter={filter}
                  columns={columns}
                  defaultVisibleColumnKeys={INVITATION_MAILS_TABLE_DEFAULT_VISIBLE_KEYS}
                  columnVisibilityStorageKey={INVITATION_MAILS_TABLE_STORAGE_KEY}
                  autoTooltipForDefaultCells
                  dropFilter={{
                    component: (
                      <DropFilterContainer>
                        <Dropdown
                          onChange={(e) => {
                            setSelectedFromEmailDraft(e.target.value);
                          }}
                          options={listFromEmail}
                          value={selectedFromEmailDraft}
                          multiselect={true}
                          showSearch={true}
                          placeholder=""
                          placeholderFlag={true}
                          label="From"
                          style={{
                            width: '100%',
                          }}
                        />

                        <Dropdown
                          onChange={(e) => {
                            setSelectedToEmailDraft(e.target.value);
                          }}
                          options={listToEmail}
                          value={selectedToEmailDraft}
                          multiselect={true}
                          showSearch={true}
                          placeholder=""
                          placeholderFlag={true}
                          label="To"
                          style={{
                            width: '100%',
                          }}
                        />

                        <Dropdown
                          onChange={(e) => {
                            setSelectedPropertyDraft(e.target.value);
                          }}
                          options={listProperty}
                          value={selectedPropertyDraft}
                          multiselect={true}
                          showSearch={true}
                          placeholder=""
                          placeholderFlag={true}
                          label="Property"
                          style={{
                            width: '100%',
                          }}
                        />
                      </DropFilterContainer>
                    ),
                    resetFunc: resetFunc,
                    applyFunc: applyFunc,
                  }}
                  dropFilterActiveCount={dropFilterActiveCount}
                  dropFilterActiveSummary={dropFilterActiveSummary}
                  pagination={{
                    ...pagination,
                    totalItems: emailsJSON.total,
                    totalPages: Math.ceil(emailsJSON.total / pagination.rowsPerPage),
                  }}
                  onChangeRowsPerPage={(value) =>
                    setPagination({
                      ...pagination,
                      rowsPerPage: value,
                      pageNumber: 0,
                    })
                  }
                  sortOptions={sortOptions}
                  onChangeSort={setSortOptions}
                  onChangeFilter={setFilter}
                  onChangePagination={setPagination}
                  title={'Invites'}
                />
                <ListExportScopeModal
                  open={exportModalOpen}
                  onClose={() => setExportModalOpen(false)}
                  tableRef={emailTableRef}
                  title="Invites"
                  currentPageRowCount={tableData.length}
                  paginationTotalItems={emailsJSON.total}
                  fullDatasetTotal={fullExportDatasetTotal}
                  enableFiltered
                  enableFull
                  enableSelected={false}
                />
              </CustomBox>
            </React.Fragment>
          ) : (
            <Loading size="large" />
          )}
        </Box>
      </Box>
    </DashboardLayoutContainer>
  );
};

const CustomTypography = styled(Typography)`
  word-break: break-all;
  overflow-wrap: anywhere;
`;

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;

const ResendInviteCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionsCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;

  &:disabled {
    cursor: not-allowed;
  }
`;
